import moment from "moment";
import SimpleAttributes from "../simple-attributes/simple-attributes";
import DistributionMultidiagram from "../distribution-multidiagram/distribution-multidiagram";
import Timeline from "../timeline/timeline";
import {
  timeFormatConfigs,
  timeFormats,
  playingTypes,
  speeds,
  layerTypesConfig
} from "../../configs";

export default {
  props: ["time-machine-state", "baselayer-id", "timemachine-filter"],
  components: {
    SimpleAttributes,
    DistributionMultidiagram,
    Timeline
  },
  watch: {
    "$store.state.map.activeLayers": function() {
      this.activeLayers = this.$store.state.map.activeLayers;
      this.loadLayersChunks();
    },
    timeMachineState: {
      handler: function(state) {
        if (state.fieldChanged) {
          this.loadLayersChunks();
          state.fieldChanged = false;
        }
      },
      deep: true,
      immediate: true
    }
  },
  data() {
    return {
      activeLayers: this.$store.state.map.activeLayers,
      currentSpeed: 1,
      isPlaying: false,
      formats: timeFormats,
      layersChunks: [],
      interval: 0,
      min: 0,
      max: 1,
      step: 1,
      intervalValue: [0, 1],
      pointValue: 0,
      isTimelineLoading: false,
      playingTypes,
      speeds
    };
  },
  computed: {
    getFormatType() {
      switch (this.timeMachineState.format) {
        case "year":
          return "year";
        case "month":
          return "month";
        case "day":
          return "date";
        case "hour":
        case "minute":
        case "second":
          return "datetime";
      }
    },
    getDatePickerFormat() {
      switch (this.timeMachineState.format) {
        case "hour":
          return "yyyy-MM-dd HH";
        case "minute":
          return "yyyy-MM-dd HH:mm";
        case "second":
          return "yyyy-MM-dd HH:mm:ss";
      }
    }
  },
  methods: {
    changePlayingType(type) {
      this.timeMachineState.type = type;
      this.$emit("refresh");
    },
    changeFormat() {
      this.changeSliderMinMaxValues();
      this.changeSliderValue();
      this.changeTime();
    },
    isDisabledFormat(format) {
      const { min, max } = this.timeMachineState;
      const diffFormat = `${format}s`;
      const minTime = moment(min);
      const maxTime = moment(max);

      return maxTime.diff(minTime, diffFormat) === 0;
    },
    changeSliderValue() {
      const { min, range, point, format, type } = this.timeMachineState;
      const minTime = moment(min);
      const diffFormat = `${format}s`;

      if (type === "interval") {
        const rangeMinValue = moment(range[0]);
        let rangeMaxValue = moment(range[1]);
        if (format === "year") rangeMaxValue = rangeMaxValue.add(1, "month");
        const minDiff = rangeMinValue.diff(minTime, diffFormat);
        const maxDiff = rangeMaxValue.diff(minTime, diffFormat);
        this.$set(this, "intervalValue", [minDiff, maxDiff]);
      } else {
        const pointValue = moment(point);
        const diff = pointValue.diff(minTime, diffFormat);
        this.$set(this, "pointValue", diff);
      }
    },
    changeSliderMinMaxValues() {
      const { min, max, format } = this.timeMachineState;
      const a = moment(min);
      let b = moment(max);
      if (format === "year") b = b.add(1, "month");
      const diffFormat = `${format}s`;
      const diff = b.diff(a, diffFormat);

      this.min = 0;
      this.max = diff;
    },
    parseTooltipTimeValue(value) {
      const { min, format } = this.timeMachineState;
      const diffFormat = `${format}s`;
      const time = moment(min).add(value, diffFormat);

      return time.format(timeFormatConfigs[this.timeMachineState.format]);
    },
    changeTime() {
      const { min, format, type } = this.timeMachineState;
      const diffFormat = `${format}s`;

      if (type === "interval") {
        const minTime = moment(min).add(this.intervalValue[0], diffFormat);
        const maxTime = moment(min).add(this.intervalValue[1], diffFormat);
        this.timeMachineState.range[0] = minTime.valueOf();
        this.timeMachineState.range[1] = maxTime.valueOf();
      } else {
        const time = moment(min).add(this.pointValue, diffFormat);
        this.timeMachineState.point = time.valueOf();
      }
      this.$emit("refresh");
    },
    closeTimemachine() {
      this.$emit("close");
    },
    playClickHandler() {
      if (this.isPlaying) this.stop();
      else this.play();
    },
    play() {
      const { max } = this;

      this.isPlaying = true;

      this.interval = setInterval(() => {
        if (this.timeMachineState.type === "interval") {
          const nextStepMin = this.intervalValue[0] + 1;
          const nextStepMax = this.intervalValue[1] + 1;

          if (nextStepMax <= max) {
            this.$set(this, "intervalValue", [nextStepMin, nextStepMax]);
          } else {
            this.intervalValue[1] = max;
            this.stop();
          }
          this.changeTime();
          this.$forceUpdate();
        } else {
          const nextStep = this.pointValue + 1;

          if (nextStep <= max) {
            this.pointValue++;
          } else {
            this.pointValue = max;
            this.stop();
          }
          this.changeTime();
          this.$forceUpdate();
        }
      }, 1600 / this.currentSpeed);
    },
    refreshPlaying() {
      const { min } = this;

      if (this.timeMachineState.type === "interval") {
        const delta = this.intervalValue[1] - this.intervalValue[0];
        this.$set(this, "intervalValue", [min, min + delta]);
      } else {
        this.pointValue = min;
      }
      if (this.isPlaying) this.stop();
      this.changeTime();
      this.play();
    },
    stop() {
      clearInterval(this.interval);
      this.isPlaying = false;
      this.interval = null;
    },
    changeSpeed(value) {
      this.currentSpeed = value;

      if (this.isPlaying) {
        this.stop();
        this.play();
      }
    },
    changeTimemachineRange() {
      this.loadLayersChunks();
    },
    async loadLayersChunks() {
      const { attribute, max, min, isNested, sourceId } = this.timeMachineState;
      const filteredLayers = this.activeLayers.filter(
        id =>
          this.$parent.objectFields[id].find(f => f.title === attribute) ||
          id === this.timeMachineState.layerId
      );

      this.isTimelineLoading = true;

      this.changeSliderMinMaxValues();
      this.changeSliderValue();

      this.layersChunks.splice(0, this.layersChunks.length);

      for (let i = 0; i < filteredLayers.length; i++) {
        const layerId = filteredLayers[i];
        const requestId = this.$store.state.profiles.sourceIdById[layerId];
        const from = isNested ? min : moment(min).format();
        const to = isNested ? max : moment(max).format();

        const config = {
          where: [
            {
              field: attribute,
              op: "between",
              type: "AND",
              value: `${from}/${to}`
            }
          ]
        };
        const url = `chunk/${
          isNested ? sourceId : requestId
        }?config=${JSON.stringify(
          config
        )}&chunk_size=80&chunk_field=${attribute}`;
        const { data } = await this.$store.dispatch("REQUEST", { url });
        this.layersChunks.push({
          id: layerId,
          chunks: data,
          allCount: this.$parent.objectsCounts[layerId],
          color: this.getLayerColor(layerId)
        });
      }
      this.isTimelineLoading = false;
    },
    getLayerColor(id) {
      const sourceId = this.$store.state.profiles.sourceIdById[id];
      const { geom_type } = this.$store.state.datasources[sourceId];
      const styleConfig = this.$store.state.map.styleConfig[id];
      const field = layerTypesConfig[geom_type];
      const color = styleConfig.style.paint[`${field}-color`];
      return color;
    },
    changeFiltersField(key, value, filterId) {
      this.$emit("change-field", { key, value, filterId });
    }
  },
  async created() {
    await this.loadLayersChunks();
    this.changeTime();
  }
};
